import React from "react";
import { makeStyles } from "@mui/styles";
import {
  Grid,
  Paper,
  Container,
  Typography,
  Slide,
  Hidden,
  Button,
} from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import { FHButton } from "../FHButton";
import CloseIcon from "@mui/icons-material/Close";
import { RichContentArea } from "../RichContentArea";
import { FHModal } from "../FHModal/index";
import { useIsMobile } from "../../../hooks/hooks";
const iconBreak = "@media (max-width: 365px)";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    zIndex: 1300,
  },
  muiContainer: {
    marginBottom: 0,
    padding: 0,
  },
  container: {
    top: "auto",
    left: 0,
    right: 0,
    bottom: 0,
    height: "auto",
    maxHeight: "100%",
    position: "fixed",
    borderRadius: 0,
    padding: theme.spacing(2),
  },
  buttonContainer: {
    "& .MuiButton-root": {
      padding: theme.spacing(0.6, 0.8),
      whiteSpace: "nowrap",
    },
  },
  iconContainer: {
    marginRight: theme.spacing(1),
    "& > svg": {
      width: 40,
      height: 40,
    },
  },
  petIconContainer: {
    "& > svg": {
      width: 20,
      height: 20,
      position: "relative",
      top: 3,
      left: 2,
    },
  },
  closeButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.secondary.main,
    borderRadius: "10px",
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
    padding: theme.spacing(0.6, 0.8),
    minWidth: "auto",
    [theme.breakpoints.up("md")]: {
      height: "100%",
    },
    "&:hover": {
      color: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.main,
      borderWidth: 2,
    },
  },
  stickyInfo: {
    [iconBreak]: {
      "& .main-icon": {
        display: "none",
      },
    },
    [theme.breakpoints.down("md")]: {
      flexDirection: "row-reverse",
    },
  },
}));

export type StickyLabels = {
  pet: string;
  pets: string;
  date: string;
  dates: string;
  guest: string;
  guests: string;
  returnButton: string;
  cta: string;
  closeTitle: string;
  closeDescription: string;
  closeConfirmButton: string;
  closeCancelButton: string;
};

export type StickyIcons = {
  main?: JSX.Element;
  pet?: JSX.Element;
  close?: JSX.Element;
};

export type StickyCheckoutNotificationProps = {
  returnButtonUrl?: string;
  returnButtonOnClick?: () => void;
  ctaButtonDisabled?: boolean;
  onCtaButtonClick?: () => void;
  isoDateFrom?: string;
  isoDateTo?: string;
  noOfGuests?: number;
  noOfPets?: number;
  show?: boolean;
  labels?: StickyLabels;
  icons?: StickyIcons;
  onCloseCancel?: () => void;
  onCloseConfirm?: () => void;
  showFHModal?: boolean;
  title?: string;
  subtitle?: string;
};

export const StickyCheckoutNotification: React.FC<StickyCheckoutNotificationProps> =
  ({
    returnButtonUrl,
    returnButtonOnClick,
    ctaButtonDisabled = false,
    isoDateFrom,
    isoDateTo,
    noOfGuests,
    noOfPets,
    show = false,
    onCtaButtonClick,
    labels,
    icons,
    onCloseCancel,
    onCloseConfirm,
    showFHModal,
    title,
    subtitle,
  }: StickyCheckoutNotificationProps) => {
    const classes = useStyles();

    const [modalOpen, setModalOpen] = React.useState(false);
    const isMobile = useIsMobile();

    const getFormattedDate = (isoDateString: string) =>
      new Date(isoDateString).toLocaleString("en-gb", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

    const dateFrom = isoDateFrom && getFormattedDate(isoDateFrom);
    const dateTo = isoDateTo && getFormattedDate(isoDateTo);

    if (show && dateFrom === "Invalid Date") {
      console.error("Warning: dateFrom is invalid");
    }
    if (show && dateTo === "Invalid Date") {
      console.error("Warning: dateTo is invalid");
    }

    const handleCloseConfirm = () => onCloseConfirm && onCloseConfirm();

    const renderCloseButton = () => (
      <Button
        color="primary"
        variant="outlined"
        data-testid="test-extra-close-button"
        className={classes.closeButton}
        onClick={() => {
          if (showFHModal) {
            setModalOpen(true);
          } else {
            handleCloseConfirm();
          }
        }}
      >
        {icons?.close || <CloseIcon />}
      </Button>
    );

    return (
      <div
        className={classes.root}
        data-testid="test-extra-notification-sticky"
      >
        <Slide direction="up" in={show} timeout={500}>
          <Paper className={classes.container}>
            <Container maxWidth="lg" className={classes.muiContainer}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4} lg={6}>
                  <Grid
                    container
                    spacing={1}
                    justifyContent="space-between"
                    className={classes.stickyInfo}
                  >
                    <Hidden mdUp>
                      <Grid item>{renderCloseButton()}</Grid>
                    </Hidden>
                    <Grid item>
                      <Grid container>
                        <Grid
                          item
                          className={`main-icon ${classes.iconContainer}`}
                        >
                          {icons?.main || <ShoppingCartOutlinedIcon />}
                        </Grid>
                        <Grid item>
                          {title && (
                            <Typography data-testid="sticky-title">
                              {title}
                            </Typography>
                          )}
                          {subtitle && (
                            <Typography
                              variant="body2"
                              data-testid="sticky-subtitle"
                            >
                              {subtitle}
                            </Typography>
                          )}
                          {dateFrom && dateTo && (
                            <Typography>
                              <b>{labels?.dates}: </b>
                              {dateFrom} &ndash; {dateTo}
                            </Typography>
                          )}

                          {typeof noOfGuests !== "undefined" && (
                            <Typography>
                              <b>
                                {noOfGuests === 1
                                  ? labels?.guest
                                  : labels?.guests}
                                :
                              </b>{" "}
                              {noOfGuests}{" "}
                              {noOfPets && (
                                <span
                                  className={classes.petIconContainer}
                                  data-testid="test-extra-sticky-pets"
                                >
                                  {`+ ${noOfPets} ${
                                    noOfPets === 1 ? labels?.pet : labels?.pets
                                  }`}{" "}
                                  {icons?.pet || <PetsOutlinedIcon />}
                                </span>
                              )}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={8} lg={6}>
                  <Grid
                    container
                    className={classes.buttonContainer}
                    spacing={1}
                    direction="row-reverse"
                  >
                    <Hidden mdDown>
                      <Grid item>{renderCloseButton()}</Grid>
                    </Hidden>
                    {labels?.cta && onCtaButtonClick && (
                      <Grid item xs={12} md="auto">
                        <FHButton
                          fhStyle="primary"
                          fhSize="md"
                          fullWidth={isMobile ? true : false}
                          disabled={ctaButtonDisabled}
                          onClick={onCtaButtonClick}
                          data-testid="test-extra-sticky-cta-button"
                        >
                          {labels?.cta}
                        </FHButton>
                      </Grid>
                    )}
                    {(returnButtonOnClick || returnButtonUrl) && (
                      <Grid item xs={12} md="auto">
                        <FHButton
                          href={returnButtonUrl}
                          onClick={returnButtonOnClick}
                          fhStyle="secondary"
                          fhSize="md"
                          fullWidth={isMobile ? true : false}
                          data-testid="test-return-button"
                        >
                          {labels?.returnButton || "Return"}
                        </FHButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </Paper>
        </Slide>
        <FHModal
          open={modalOpen}
          handleClose={() => {
            setModalOpen(false);
            onCloseCancel && onCloseCancel();
          }}
          handleConfirm={handleCloseConfirm}
          title={labels?.closeTitle || ""}
          description={
            <RichContentArea>
              <div
                dangerouslySetInnerHTML={{
                  __html: labels?.closeDescription || "",
                }}
              />
            </RichContentArea>
          }
          confirmCTALabel={labels?.closeConfirmButton || ""}
          cancelCTALabel={labels?.closeCancelButton || ""}
          data-testid="test-extra-fh-modal"
        />
      </div>
    );
  };
